import { Extension } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { createRoot } from 'react-dom/client';
import { createElement, createRef } from 'react';
import type { SuggestionProps } from '@tiptap/suggestion';
import { filterCommands, type SlashCommand } from './slash-commands';
import { SlashMenu, type SlashMenuHandle } from './slash-menu';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';

// ── Portal management ─────────────────────────────────────────────────────────
// FloatingPanel owns fixed positioning, z-index and the --fp-* vars via its
// CSS module. We mount a React portal root once and reuse it across onStart /
// onUpdate cycles; FloatingPanel handles the actual createPortal to body.

function createSuggestionRenderer() {
  let container: HTMLDivElement | null = null;
  let root: ReturnType<typeof createRoot> | null = null;
  // Imperative ref to the SlashMenu so onKeyDown reaches the real list state.
  const menuRef = createRef<SlashMenuHandle>();

  function getOrCreateContainer() {
    if (!container) {
      container = document.createElement('div');
      container.dataset.slashMenuRoot = 'true';
      document.body.appendChild(container);
      root = createRoot(container);
    }
    return { container, root: root! };
  }

  function render(props: SuggestionProps<SlashCommand, SlashCommand>) {
    // Don't render when there are no items (e.g. /? which opens the modal instead)
    if (props.items.length === 0 && (props.query ?? '').length > 0) {
      destroy();
      return;
    }
    const { root: r } = getOrCreateContainer();

    // Compute caret position for FloatingPanel
    let top = 0;
    let left = 0;
    if (props.decorationNode) {
      const rect = (props.decorationNode as Element).getBoundingClientRect();
      top = rect.bottom + 8;
      left = rect.left;
    } else if (props.clientRect) {
      const rect = props.clientRect();
      if (rect) {
        top = rect.bottom + 8;
        left = rect.left;
      }
    }

    // FloatingPanel renders a createPortal to document.body with fixed
    // positioning and correct z-index via its CSS module (--fp-top, --fp-left,
    // --fp-z-index). No inline style violations here.
    const menu = createElement(SlashMenu, {
      ref: menuRef,
      items: props.items,
      isQuerying: (props.query ?? '').length > 0,
      onSelect: (cmd: SlashCommand) => {
        // props.command deletes the typed /query text then runs the command.
        props.command(cmd);
      },
    });
    r.render(
      createElement(FloatingPanel, { top, left, children: menu }),
    );
  }

  function destroy() {
    if (root) {
      // Capture locals before nulling — prevents a race where onExit followed
      // immediately by onStart on the same tick can null the new render root.
      const r = root;
      const c = container;
      root = null;
      container = null;
      // Unmount asynchronously to avoid React 18 "render in commit phase" warning.
      setTimeout(() => {
        r.unmount();
        c?.remove();
      }, 0);
    }
  }

  return { render, destroy, menuRef };
}

export const SlashSuggestionExtension = Extension.create({
  name: 'slashSuggestion',

  addProseMirrorPlugins() {
    const { render, destroy, menuRef } = createSuggestionRenderer();

    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        allowSpaces: false,
        startOfLine: false,
        items: ({ query }) => query === '?' ? [] : filterCommands(query),
        command: ({ editor, range, props: cmd }) => {
          // Delete the typed /query text, then run the command
          editor.chain().focus().deleteRange(range).run();
          cmd.run(editor);
        },
        render: () => ({
          onStart: (props) => render(props),
          onUpdate: (props) => render(props),
          onExit: () => destroy(),
          onKeyDown: ({ event }) => {
            // Delegate directly to the SlashMenu's imperative handle so the
            // selection state update actually reaches the React component.
            // (Dispatching a DOM event on the outer container would go upward
            // in the DOM, never reaching the child listbox's React handler.)
            if (menuRef.current) {
              menuRef.current.onKeyDown(event);
              // Suppress ArrowUp/Down/Enter/Escape from reaching the editor
              if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) return true;
            }
            return false;
          },
        }),
      }),
    ];
  },
});
